//@ts-nocheck
// INDONESIA COMPATIBILITY 3-BOX — Two-person emotional compatibility form
// Person A and Person B each fill: Box1 (calm type) + Box2 (DOB) + Box3 (moment state)
// Tone: calm, gentle, respectful, low-intensity (Indonesia style)
import React, { useState, useCallback } from "react";
import { useLaneCopy } from "../../utils/laneCopy";
import { useIndonesiaV2Language } from "./indonesiaV2/indonesiaV2Language";
import { Indonesia3BoxData } from "./Indonesia3Box";
import { useAuth } from "../../contexts/AuthContext";

// ─── Design tokens (warm brown Indonesian palette) ────────────────────────────
const C = {
  bg: "#0f0a14",
  card: "#1a131f",
  border: "rgba(255,255,255,0.1)",
  beige: "#221a29",
  textDark: "#f7f3fa",
  textMuted: "#c8bfd1",
  accent: "#a78bff",
  accentSoft: "rgba(167,139,255,0.14)",
  accentBorder: "rgba(167,139,255,0.4)",
  accentB: "#7ee2c0", // teal-green for Person B
  accentBSoft: "rgba(126,226,192,0.12)",
  accentBBorder: "rgba(126,226,192,0.4)",
  white: "#0f0a14",
};

// ─── Options ──────────────────────────────────────────────────────────────────
const COPY = {
  en: {
    pageTitle: "💞 Emotional Compatibility",
    pageSubtitle:
      "Fill in both emotional profiles to explore your emotional compatibility.",

    back: "‹ Back",

    personA: "Person A — Your Profile",
    personB: "Person B — Partner Profile",

    box1: "Inner Calm",
    box2: "Date of Birth",
    box3: "Current State",

    day: "Day",
    month: "Month",
    year: "Year",
    dayPlaceholder: "DD",
    monthPlaceholder: "MM",
    yearPlaceholder: "YYYY",

    submit: "View Emotional Compatibility",
    loadingButton: "Reading...",

    loadingText: "Reading the emotional rhythm between both of you...",

    box1Options: [
      {
        key: "soft_calm",
        label: "Soft Calm",
        desc: "Usually calm and not easily rushed.",
      },
      {
        key: "balanced_gentle",
        label: "Balanced Gentle",
        desc: "Gentle, stable, and adapts naturally.",
      },
      {
        key: "deep_quiet",
        label: "Deep Quiet",
        desc: "Processes emotions quietly and deeply.",
      },
    ],

    box3Options: [
      {
        key: "calm_moment",
        label: "Calm Moment",
        desc: "Feeling calm and emotionally steady.",
      },
      {
        key: "heavy_moment",
        label: "Heavy Moment",
        desc: "Carrying a little emotional weight.",
      },
      {
        key: "need_space",
        label: "Need Space",
        desc: "Needs a little quiet space.",
      },
      {
        key: "quiet_focus",
        label: "Quiet Focus",
        desc: "Focused in a calm and gentle way.",
      },
    ],
  },

  id: {
    pageTitle: "💞 Kecocokan Emosional",
    pageSubtitle:
      "Isi profil emosional Anda berdua untuk melihat kecocokan ritme.",

    back: "‹ Kembali",

    personA: "Orang A — Profil Anda",
    personB: "Orang B — Profil Pasangan",

    box1: "Dasar Ketenangan",
    box2: "Tanggal Lahir",
    box3: "Keadaan Saat Ini",

    day: "Tanggal",
    month: "Bulan",
    year: "Tahun",
    dayPlaceholder: "TGL",
    monthPlaceholder: "BLN",
    yearPlaceholder: "THN",

    submit: "Lihat Kecocokan Emosional",
    loadingButton: "Sedang membaca...",

    loadingText: "Sedang membaca ritme emosi Anda berdua...",

    box1Options: [
      {
        key: "soft_calm",
        label: "Tenang Lembut",
        desc: "Cenderung tenang dan tidak terburu-buru.",
      },
      {
        key: "balanced_gentle",
        label: "Seimbang & Lembut",
        desc: "Lembut, stabil, dan mudah menyesuaikan diri.",
      },
      {
        key: "deep_quiet",
        label: "Diam yang Dalam",
        desc: "Memproses banyak hal secara diam-diam dan mendalam.",
      },
    ],

    box3Options: [
      {
        key: "calm_moment",
        label: "Momen Tenang",
        desc: "Merasa cukup tenang dan terkendali.",
      },
      {
        key: "heavy_moment",
        label: "Momen Berat",
        desc: "Ada sedikit beban yang dirasakan.",
      },
      {
        key: "need_space",
        label: "Butuh Ruang",
        desc: "Butuh ruang sejenak untuk menenangkan diri.",
      },
      {
        key: "quiet_focus",
        label: "Fokus Tenang",
        desc: "Sedang fokus dengan cara yang lembut dan diam.",
      },
    ],
  },
} as const;

type Lang = keyof typeof COPY;
type Copy = (typeof COPY)[Lang];

const YEARS = Array.from({ length: 80 }, (_, i) =>
  String(new Date().getFullYear() - i),
);
const MONTHS = Array.from({ length: 12 }, (_, i) =>
  String(i + 1).padStart(2, "0"),
);
const DAYS = Array.from({ length: 31 }, (_, i) =>
  String(i + 1).padStart(2, "0"),
);

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  wrap: {
    flex: 1,
    overflowY: "auto" as const,
    background: C.bg,
    minHeight: "100%",
    display: "flex" as const,
    flexDirection: "column" as const,
  },
  content: {
    padding: "24px 16px 48px",
    maxWidth: 520,
    margin: "0 auto",
    width: "100%",
    display: "flex" as const,
    flexDirection: "column" as const,
  },
  pageTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: C.textDark,
    textAlign: "center" as const,
    marginBottom: 4,
  },
  pageSubtitle: {
    fontSize: 13,
    color: C.textMuted,
    textAlign: "center" as const,
    marginBottom: 24,
    lineHeight: 1.6,
  },
  personCard: (isB: boolean) => ({
    background: C.card,
    border: `1.5px solid ${isB ? C.accentBBorder : C.accentBorder}`,
    borderRadius: 16,
    padding: "18px 16px",
    marginBottom: 16,
  }),
  personHeader: (isB: boolean) => ({
    fontSize: 12,
    fontWeight: 700,
    color: isB ? C.accentB : C.accent,
    textTransform: "uppercase" as const,
    letterSpacing: "0.1em",
    marginBottom: 14,
    paddingBottom: 10,
    borderBottom: `1px solid ${isB ? C.accentBBorder : C.accentBorder}`,
  }),
  sectionLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: C.textMuted,
    marginBottom: 8,
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
  },
  optionRow: (active: boolean, isB: boolean) => ({
    borderRadius: 10,
    background: active ? (isB ? C.accentBSoft : C.accentSoft) : C.card,
    border: `1.5px solid ${active ? (isB ? C.accentBBorder : C.accentBorder) : C.border}`,
    padding: "10px 12px",
    marginBottom: 8,
    cursor: "pointer" as const,
    transition: "all 0.15s",
  }),
  optionLabel: (active: boolean, isB: boolean) => ({
    fontSize: 14,
    fontWeight: 600,
    color: active ? (isB ? C.accentB : C.accent) : C.textDark,
    marginBottom: 2,
  }),
  optionDesc: { fontSize: 12, color: C.textMuted, lineHeight: 1.5 },
  dobRow: {
    display: "grid" as const,
    gridTemplateColumns: "1fr 1fr 1.4fr",
    gap: 8,
    marginBottom: 4,
  },
  select: {
    height: 42,
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    background: C.card,
    color: C.textDark,
    padding: "0 8px",
    fontSize: 13,
    width: "100%",
    boxSizing: "border-box" as const,
  },
  fieldLabel: {
    fontSize: 11,
    color: C.textMuted,
    textAlign: "center" as const,
    marginTop: 4,
  },
  divider: {
    display: "flex" as const,
    alignItems: "center" as const,
    gap: 10,
    margin: "8px 0 16px",
  },
  dividerLine: { flex: 1, height: 1, background: C.border },
  dividerDot: { fontSize: 12, color: C.textMuted },
  btn: (enabled: boolean) => ({
    width: "100%",
    height: 50,
    borderRadius: 14,
    background: enabled ? C.accent : "rgba(255,255,255,0.08)",
    color: enabled ? C.white : C.textMuted,
    fontSize: 15,
    fontWeight: 600,
    border: "none",
    cursor: enabled ? ("pointer" as const) : ("not-allowed" as const),
    transition: "all 0.18s",
    marginTop: 8,
  }),
};

// ─── Single person block ──────────────────────────────────────────────────────
interface PersonBlockProps {
  label: string;
  isB?: boolean;
  data: Partial<Indonesia3BoxData> & {
    dobD?: string;
    dobM?: string;
    dobY?: string;
  };
  t: typeof COPY.en;
  onChange: (patch: any) => void;
}

const PersonBlock: React.FC<PersonBlockProps> = ({
  label,
  isB = false,
  data,
  t,
  onChange,
}) => (
  <div style={S.personCard(isB)}>
    <div style={S.personHeader(isB)}>{label}</div>

    {/* Box 1 — Calm Type */}
    <div style={S.sectionLabel}>{t.box1}</div>
    {t.box1Options.map((opt) => {
      const active = data.inner_calm_type === opt.key;
      return (
        <div
          key={opt.key}
          style={S.optionRow(active, isB)}
          onClick={() => onChange({ inner_calm_type: opt.key })}
        >
          <div style={S.optionLabel(active, isB)}>{opt.label}</div>
          <div style={S.optionDesc}>{opt.desc}</div>
        </div>
      );
    })}

    {/* Box 2 — DOB */}
    <div style={{ marginTop: 14, marginBottom: 4 }}>
      <div style={S.sectionLabel}>{t.box2}</div>
      <div style={S.dobRow}>
        <div>
          <select
            style={S.select}
            value={data.dobD || ""}
            onChange={(e) => onChange({ dobD: e.target.value })}
          >
            <option value="">{t.dayPlaceholder}</option>
            {DAYS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <div style={S.fieldLabel}>{t.day}</div>
        </div>
        <div>
          <select
            style={S.select}
            value={data.dobM || ""}
            onChange={(e) => onChange({ dobM: e.target.value })}
          >
            <option value="">{t.monthPlaceholder}</option>
            {MONTHS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <div style={S.fieldLabel}>{t.month}</div>
        </div>
        <div>
          <select
            style={S.select}
            value={data.dobY || ""}
            onChange={(e) => onChange({ dobY: e.target.value })}
          >
            <option value="">{t.yearPlaceholder}</option>
            {YEARS.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <div style={S.fieldLabel}>{t.year}</div>
        </div>
      </div>
    </div>

    {/* Box 3 — Moment State */}
    <div style={{ marginTop: 14 }}>
      <div style={S.sectionLabel}>{t.box3}</div>
      {t.box3Options.map((opt) => {
        const active = data.moment_state === opt.key;
        return (
          <div
            key={opt.key}
            style={S.optionRow(active, isB)}
            onClick={() => onChange({ moment_state: opt.key })}
          >
            <div style={S.optionLabel(active, isB)}>{opt.label}</div>
            <div style={S.optionDesc}>{opt.desc}</div>
          </div>
        );
      })}
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
interface IndonesiaCompatibility3BoxProps {
  onBack?: () => void;
  onSubmit: (
    selfData: Indonesia3BoxData,
    partnerData: Indonesia3BoxData,
  ) => void;
  isLoading?: boolean;
}

type PersonState = {
  inner_calm_type: string | null;
  moment_state: string | null;
  dobD: string;
  dobM: string;
  dobY: string;
};

const emptyPerson = (): PersonState => ({
  inner_calm_type: null,
  moment_state: null,
  dobD: "",
  dobM: "",
  dobY: "",
});

const IndonesiaCompatibility3Box: React.FC<IndonesiaCompatibility3BoxProps> = ({
  onBack,
  onSubmit,
  isLoading = false,
}) => {
  const [self, setSelf] = useState<PersonState>(emptyPerson());
  const [partner, setPartner] = useState<PersonState>(emptyPerson());
  // Get user's region from auth context to determine language
  const { user } = useAuth();
  const lang: Lang = useIndonesiaV2Language();
  const t = useLaneCopy("idcompat", COPY);

  const isComplete = (p: PersonState) =>
    !!(p.inner_calm_type && p.moment_state && p.dobD && p.dobM && p.dobY);

  const bothComplete = isComplete(self) && isComplete(partner);

  const handleSubmit = useCallback(() => {
    if (!bothComplete || isLoading) return;
    const toData = (p: PersonState): Indonesia3BoxData => ({
      inner_calm_type: p.inner_calm_type as any,
      dob: `${p.dobD}-${p.dobM}-${p.dobY}`,
      moment_state: p.moment_state as any,
    });
    onSubmit(toData(self), toData(partner));
  }, [bothComplete, isLoading, self, partner, onSubmit]);

  if (isLoading) {
    return (
      <div style={S.wrap}>
        <div
          style={{
            ...S.content,
            alignItems: "center",
            justifyContent: "center",
            minHeight: 340,
            paddingTop: 80,
          }}
        >
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: C.accentSoft,
              border: `2px solid ${C.accentBorder}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 32,
              marginBottom: 24,
            }}
          >
            🌿
          </div>
          <p
            style={{
              fontSize: 15,
              color: C.textMuted,
              textAlign: "center",
              lineHeight: 1.65,
            }}
          >
            {t.loadingText}
          </p>
          <div style={{ display: "flex", gap: 8, marginTop: 28 }}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: C.accent,
                  opacity: 0.5,
                  animation: `pulse-dot 1.4s ease-in-out ${i * 0.22}s infinite`,
                }}
              />
            ))}
          </div>
          <style>{`@keyframes pulse-dot { 0%,100%{opacity:0.25;transform:scale(0.85)} 50%{opacity:0.9;transform:scale(1.1)} }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      <div style={S.content}>
        {onBack && (
          <button
            style={{
              alignSelf: "flex-start",
              background: "none",
              border: "none",
              color: C.textMuted,
              fontSize: 13,
              cursor: "pointer",
              marginBottom: 20,
              padding: 0,
            }}
            onClick={onBack}
          >
            {t.back}
          </button>
        )}

        <h1 style={S.pageTitle}>{t.pageTitle}</h1>
        <p style={S.pageSubtitle}>{t.pageSubtitle}</p>

        {/* Person A */}
        <PersonBlock
          label={t.personA}
          isB={false}
          data={self}
          t={t}
          onChange={(patch) => setSelf((prev) => ({ ...prev, ...patch }))}
        />

        {/* Divider */}
        <div style={S.divider}>
          <div style={S.dividerLine} />
          <span style={S.dividerDot}>✦</span>
          <div style={S.dividerLine} />
        </div>

        {/* Person B */}
        <PersonBlock
          label={t.personB}
          isB={true}
          data={partner}
          t={t}
          onChange={(patch) => setPartner((prev) => ({ ...prev, ...patch }))}
        />

        <button
          style={S.btn(bothComplete && !isLoading)}
          disabled={!bothComplete || isLoading}
          onClick={handleSubmit}
        >
          {isLoading ? t.loadingButton : t.submit}
        </button>
      </div>
    </div>
  );
};

export default IndonesiaCompatibility3Box;
